import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import Button from './button';
import LoadingInButton from './loadingInButton';

/**
 * A reusable confirmation popover component that appears at a specific position
 * with an arrow pointing to the trigger element.
 */
const ConfirmationPopover = ({
  isOpen,
  onClose,
  position,
  title,
  description,
  confirmLabel,
  onConfirm,
  isLoading,
  children
}) => {
  // Handle click outside to close the popover
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.confirmation-popover') && !isLoading) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isLoading, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50" onClick={() => !isLoading && onClose()}>
      <div
        className="absolute bg-white rounded-md shadow-lg p-5 w-72 border border-gray-300 confirmation-popover"
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`
        }}
        onClick={(e) => e.stopPropagation()}>
        {/* Arrow pointing left */}
        <div
          className="absolute w-4 h-4 bg-white border-l border-b border-gray-300 transform rotate-45"
          style={{ left: '-8px', top: '20px' }}></div>

        <h4 className="font-semibold">{title}</h4>
        <p className="text-sm my-2 text-secondary">{description}</p>

        {children ? (
          <div className="mt-4">{children}</div>
        ) : (
          <div className="mt-4">
            <Button.ButtonFilled onClick={onConfirm} disabled={isLoading} isMini>
              {isLoading ? (
                <LoadingInButton size="small" position="none" loading={true} />
              ) : (
                confirmLabel
              )}
            </Button.ButtonFilled>
          </div>
        )}
      </div>
    </div>
  );
};

ConfirmationPopover.propTypes = {
  /** Whether the popover is currently visible */
  isOpen: PropTypes.bool.isRequired,
  /** Function to call when the popover should close */
  onClose: PropTypes.func.isRequired,
  /** Position object with top and left coordinates */
  position: PropTypes.shape({
    top: PropTypes.number.isRequired,
    left: PropTypes.number.isRequired
  }).isRequired,
  /** Title text for the popover */
  title: PropTypes.string.isRequired,
  /** Description text for the popover */
  description: PropTypes.string.isRequired,
  /** Label for the confirmation button */
  confirmLabel: PropTypes.string,
  /** Function to call when the user confirms */
  onConfirm: PropTypes.func,
  /** Whether the confirmation action is currently loading */
  isLoading: PropTypes.bool,
  /** Optional custom content to replace the default button */
  children: PropTypes.node
};

ConfirmationPopover.defaultProps = {
  confirmLabel: 'Confirm',
  onConfirm: () => {},
  isLoading: false,
  children: null
};

export default ConfirmationPopover;
